/**
 * The drying run: what a lot weighed before and after, how long it took, and on which machine.
 *
 * Shape and vocabulary come from seedtrac's Drying Slip (manual p319-320), whose grid reads
 * "Existing SLOC | Updated SLOC | After Drying (NoB, Qty) | Drying Loss (Qty, %) | Drying Start |
 * Drying End | Total D.Time | Drying Details", and whose worked row is
 * `WH-01/B17/16[2] 29.100 → WH-01/B17/16[2] 28.500`, loss 0.600 (2.06 %), `Machine 01`.
 *
 * The LOSS QUANTITY is not computed here: it is `computeDifference` with storage on both sides,
 * which already means "what went in minus what came out" and already books itself through
 * `applyAutoLossGain`. What lives here is the two things that grid shows and nothing else
 * calculates — the loss as a percentage, and the elapsed time.
 */
import { toDecimal, type DecimalInput } from "./decimal";
import { z } from "zod";

/**
 * Drying loss as a percentage of what went in, to 2 decimals — seedtrac prints `2.06`.
 *
 * Null when there was nothing to dry: a zero or absent "before" figure has no percentage, and
 * returning 0 there would read as a perfect run rather than as no run at all.
 */
export function dryingLossPct(
  beforeQty: DecimalInput,
  afterQty: DecimalInput
): string | null {
  const before = toDecimal(beforeQty);
  if (before.lte(0)) return null;
  const after = toDecimal(afterQty);
  return before.minus(after).div(before).times(100).toFixed(2);
}

/**
 * Elapsed drying time in whole minutes, or null when the pair does not describe a run.
 *
 * An end before its start returns null rather than a negative: the operator mistyped one of the
 * two, and a negative duration would be presented as if it were a measurement.
 */
export function dryingMinutes(
  start: string | null | undefined,
  end: string | null | undefined
): number | null {
  if (!start || !end) return null;
  const a = Date.parse(start);
  const b = Date.parse(end);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return null;
  return Math.round((b - a) / 60000);
}

/** "Total D.Time" as seedtrac shows it: `14 h 20 m`, `45 m` under the hour, `1 d 23 h 50 m` from a day. */
export function formatDuration(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes))
    return "";
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  // From a full day on, the days lead: 47 h 50 m reads 1 d 23 h 50 m (user, 2026-10-15).
  const d = Math.floor(h / 24);
  if (d) return `${d} d ${h - d * 24} h ${m - h * 60} m`;
  return h ? `${h} h ${m - h * 60} m` : `${m} m`;
}

/**
 * The drying run's own fields, stored in `document_lines.extra`.
 *
 * `extra` rather than columns for the same reason `prelim_quality` is jsonb: nothing joins,
 * filters or aggregates on a drying timestamp today. If a drying report ever needs to scan a date
 * range across millions of lines, promote them to columns then — it is a migration, not a redesign.
 */
export const dryingDetails = z
  .object({
    dryingStart: z.iso.datetime({ local: true }),
    dryingEnd: z.iso.datetime({ local: true }),
    /** The machine, free text: there is no activity or dryer master in this schema. */
    dryingDetails: z.string().max(200),
  })
  .partial();

export type DryingDetails = z.infer<typeof dryingDetails>;
